'use client';

import { ReactNode } from 'react';
import { css, stack } from '@bts-web/utils-style-engine';
import {
  AssetImage,
  DialogBase,
  HyperLinkButtonBase,
  HyperLinkButtonBaseVariants,
  AlertBox,
} from './../../../common';
import { PoweredBy } from '../../../trade/Reusable/PoweredBy/PoweredBy';
import { ModalComponent } from '../ModalDrawerContext/ModalDrawerContext';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { RegularDismissCircle } from '../../svgIcons/RegularDismissCircle';

export type FailureModalProps = ModalComponent<{
  title: string;
  description: string | ReactNode;
  poweredByText?: string;
  tryAgainLink?: {
    label: string;
    url: string;
    visual?: HyperLinkButtonBaseVariants;
  } | null;
  cancelLink?: {
    label: string;
    url: string;
    visual?: HyperLinkButtonBaseVariants;
  } | null;
  logo?: {
    url: string;
    name: string;
  };
}>;

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  mx: 'auto',
  flex: '1',
});

export const FailureModal: FailureModalProps = ({
  title,
  description,
  tryAgainLink,
  cancelLink,
  poweredByText,
  onClose,
  logo,
}) => {
  const { t } = useClientTranslation();

  const handleClose = () => {
    onClose();
  };

  return (
    <DialogBase
      id="base-modal"
      onClose={handleClose}
      size="small"
      onMobileFullScreen
      visual="secondary"
      title={title}
      showTitleBelowHeader
      aboveTitleElement={
        <>
          {logo && (
            <div
              className={css({
                pt: 'medium',
              })}
            >
              <div
                className={css({
                  display: 'inline-flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '100%',
                  p: {
                    base: 'extra_small_2',
                    lg: 'small',
                  },
                  backgroundColor: 'neutrals.fill_quinary',
                  boxShadow: `0px 0px 18px 0px rgba(0, 0, 0, 0.25)`,
                })}
              >
                <AssetImage
                  src={logo.url}
                  width={48}
                  height={48}
                  alt={logo.name}
                  priority
                  testId="trade-asset-logo"
                  aria-label="asset logo"
                />
              </div>
            </div>
          )}
        </>
      }
    >
      <div className={containerStyles}>
        <AlertBox variant="error-full">
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: 'medium',
              alignItems: 'center',
              py: 'medium',
              px: 'extra_small_2',
            })}
          >
            <RegularDismissCircle
              size={24}
              data-element="icon-visual"
              data-testid="icon-error"
            />

            <p>{description}</p>
          </div>
        </AlertBox>
      </div>

      <div
        className={stack({
          marginInlineStart: 'auto',
          marginInlineEnd: 'auto',
          maxWidth: '390px',
          width: '100%',
          gap: 'small',
          alignItems: 'center',
        })}
      >
        <PoweredBy
          centered
          noMargins
          value={poweredByText || t('powered_by_@tenant')}
          showLogo={!poweredByText}
        />

        {tryAgainLink && (
          <HyperLinkButtonBase
            prefetch
            visual={tryAgainLink.visual || 'accent'}
            data-testid="continue-link"
            to={tryAgainLink.url}
            onClick={handleClose}
          >
            {tryAgainLink.label}
          </HyperLinkButtonBase>
        )}

        {cancelLink && (
          <HyperLinkButtonBase
            prefetch
            visual={cancelLink.visual || 'accent'}
            data-testid="cancel-link"
            to={cancelLink.url}
            onClick={handleClose}
          >
            {cancelLink.label}
          </HyperLinkButtonBase>
        )}
      </div>
    </DialogBase>
  );
};
